/* @ts-nocheck */

(function () {
  if (customElements.get('behind-scenes-carousel')) return;

  class BehindScenesCarousel extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
      this.sectionObserver = null;
      this.isSectionVisible = true;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.activeAutoplayVideo = null;
      this.autoplayAdvanceInProgress = false;
      this.userPaused = false;
    }

    connectedCallback() {
      this.abortController = new AbortController();
      this.videos = Array.from(this.querySelectorAll('[data-behind-scenes-video]'));
      this.slides = Array.from(this.querySelectorAll('.behind-scenes-video-carousel__slide'));

      this.videos.forEach((video) => {
        video.muted = true;
        video.playsInline = true;
        video.loop = this.shouldUseNativeLoop(video);

        video.addEventListener('play', () => this.updateMediaState(video), {
          signal: this.abortController.signal,
        });
        video.addEventListener('pause', () => this.updateMediaState(video), {
          signal: this.abortController.signal,
        });
        video.addEventListener('ended', () => this.handleVideoEnded(video), {
          signal: this.abortController.signal,
        });
        video.addEventListener('click', this.handleVideoClick, {
          signal: this.abortController.signal,
        });
      });

      this.querySelectorAll('[data-behind-scenes-toggle]').forEach((button) => {
        button.addEventListener('click', this.handleToggleClick, {
          signal: this.abortController.signal,
        });
      });

      this.addEventListener('slideshow:select', this.handleSlideSelect, {
        signal: this.abortController.signal,
      });
      this.addEventListener('focusin', this.handleFocusIn, {signal: this.abortController.signal});
      this.addEventListener('focusout', this.handleFocusOut, {signal: this.abortController.signal});
      document.addEventListener('visibilitychange', this.handleVisibilityChange, {
        signal: this.abortController.signal,
      });
      this.prefersReducedMotion.addEventListener('change', this.handleMotionPreferenceChange, {
        signal: this.abortController.signal,
      });

      this.setupObservers();
      this.syncPlayback();
    }

    disconnectedCallback() {
      this.pauseAll();
      this.observer?.disconnect();
      this.sectionObserver?.disconnect();
      this.abortController.abort();
    }

    get shouldAutoplay() {
      return this.dataset.autoplay === 'true' && !this.prefersReducedMotion.matches && !this.userPaused;
    }

    get shouldAutoplayCarousel() {
      return this.shouldAutoplay && this.dataset.autoplayCarousel === 'true';
    }

    get shouldLoopCarousel() {
      return this.dataset.loopCarousel === 'true';
    }

    get slideshow() {
      return this.querySelector('slideshow-component');
    }

    setupObservers() {
      if ('IntersectionObserver' in window) {
        const scroller = this.querySelector('slideshow-slides');
        this.isSectionVisible = false;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              entry.target.toggleAttribute('data-behind-scenes-visible', entry.intersectionRatio >= 0.55);
            });
            this.syncPlayback();
          },
          {
            root: scroller || null,
            threshold: [0, 0.55, 0.85],
          }
        );

        this.slides.forEach((slide) => this.observer.observe(slide));

        this.sectionObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            this.isSectionVisible = entry ? entry.isIntersecting : true;
            this.syncPlayback();
          },
          {
            threshold: 0.15,
          }
        );
        this.sectionObserver.observe(this);
      } else {
        this.slides.forEach((slide) => slide.setAttribute('data-behind-scenes-visible', ''));
      }
    }

    handleToggleClick = (event) => {
      const media = event.currentTarget.closest('.behind-scenes-video-carousel__media');
      const video = media?.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) return;

      this.toggleVideo(video);
    };

    handleVideoClick = (event) => {
      const video = event.currentTarget;
      if (!(video instanceof HTMLVideoElement)) return;

      this.toggleVideo(video);
    };

    toggleVideo(video) {
      if (video.paused || video.ended) {
        this.userPaused = false;
        this.pauseAll(video);
        this.play(video);
      } else {
        this.userPaused = true;
        video.pause();
      }
    }

    handleFocusIn = () => this.pauseAll();

    handleFocusOut = () => {
      requestAnimationFrame(() => {
        if (!this.matches(':focus-within')) this.syncPlayback();
      });
    };

    handleSlideSelect = () => {
      this.activeAutoplayVideo = null;
      this.syncPlayback();
    };

    handleVisibilityChange = () => {
      this.syncPlayback();
    };

    handleMotionPreferenceChange = () => {
      this.syncPlayback();
    };

    syncPlayback() {
      if (document.hidden || !this.isSectionVisible) {
        this.pauseAll();
        return;
      }

      this.videos.forEach((video) => {
        video.loop = this.shouldUseNativeLoop(video);
      });
      this.pauseOffscreenVideos();

      if (!this.shouldAutoplay) return;

      const video = this.getAutoplayCandidate();
      this.pauseAll(video);

      if (video) this.play(video);
    }

    getAutoplayCandidate() {
      const visibleVideos = this.getVisibleVideos();

      if (this.activeAutoplayVideo && visibleVideos.includes(this.activeAutoplayVideo)) {
        return this.activeAutoplayVideo;
      }

      return visibleVideos[0] || null;
    }

    getVisibleVideos() {
      return this.slides
        .filter((slide) => slide.hasAttribute('data-behind-scenes-visible'))
        .map((slide) => slide.querySelector('video'))
        .filter((video) => video instanceof HTMLVideoElement);
    }

    shouldUseNativeLoop(video) {
      return video.dataset.loopVideo === 'true' && !(this.shouldAutoplay && this.videos.length > 1);
    }

    play(video) {
      video.muted = true;
      video.loop = this.shouldUseNativeLoop(video);
      this.activeAutoplayVideo = this.shouldAutoplay ? video : this.activeAutoplayVideo;
      const playPromise = video.play();

      if (playPromise?.catch) {
        playPromise.catch(() => {
          video.pause();
        });
      }
    }

    pauseAll(exceptVideo = null) {
      this.videos.forEach((video) => {
        if (video !== exceptVideo) video.pause();
      });
    }

    pauseOffscreenVideos() {
      this.videos.forEach((video) => {
        const slide = video.closest('.behind-scenes-video-carousel__slide');
        if (slide && !slide.hasAttribute('data-behind-scenes-visible')) {
          video.pause();
        }
      });
    }

    updateMediaState(video) {
      const media = video.closest('.behind-scenes-video-carousel__media');
      const button = media?.querySelector('[data-behind-scenes-toggle]');
      const isPlaying = !video.paused && !video.ended;

      media?.classList.toggle('is-playing', isPlaying);
      button?.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
    }

    handleVideoEnded(video) {
      this.updateMediaState(video);

      if (!this.shouldAutoplay || video !== this.activeAutoplayVideo) return;

      const visibleVideos = this.getVisibleVideos();
      const currentIndex = visibleVideos.indexOf(video);
      const nextVisibleVideo = visibleVideos[currentIndex + 1];

      if (nextVisibleVideo) {
        this.startAutoplayVideo(nextVisibleVideo);
        return;
      }

      if (this.shouldAutoplayCarousel) {
        if (this.advanceCarouselAfter(video)) return;

        this.activeAutoplayVideo = null;
        return;
      }

      if (video.dataset.loopVideo === 'true') {
        const firstVisibleVideo = visibleVideos[0];
        if (firstVisibleVideo) this.startAutoplayVideo(firstVisibleVideo);
      } else {
        this.activeAutoplayVideo = null;
      }
    }

    startAutoplayVideo(video) {
      video.currentTime = 0;
      this.activeAutoplayVideo = video;
      this.pauseAll(video);
      this.play(video);
    }

    advanceCarouselAfter(video) {
      const slideshow = this.slideshow;
      if (!slideshow || this.autoplayAdvanceInProgress) return false;

      const currentVideoIndex = this.videos.indexOf(video);
      const nextVideoIndex = currentVideoIndex + 1;
      const targetIndex = nextVideoIndex < this.videos.length ? nextVideoIndex : 0;

      if (nextVideoIndex >= this.videos.length && !this.shouldLoopCarousel) return false;

      this.autoplayAdvanceInProgress = true;
      this.activeAutoplayVideo = null;
      slideshow.select(targetIndex, undefined, { animate: true });

      window.setTimeout(() => {
        this.autoplayAdvanceInProgress = false;
        this.syncPlayback();
      }, 450);

      return true;
    }
  }

  customElements.define('behind-scenes-carousel', BehindScenesCarousel);
})();
